import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Button, 
  Space, 
  message, 
  Spin,
  Upload,
  Modal,
  InputNumber,
  Row,
  Col,
  Select,
  List,
  Tag,
  DatePicker
} from 'antd';
import { 
  ArrowLeftOutlined, 
  SaveOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FileOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { legislativeNoticeService } from '../../services/legislativeNoticeService';
import { attachmentService } from '../../services/attachmentService';
import { useAuth } from '../../contexts/AuthContext';
import { LegislativeNotice, LegislativeNoticeCategory, Attachment } from '../../types';

import dayjs from 'dayjs';
import '../../styles/contentStyles.css';
import { insertTable } from '../../utils/tableUtils';
import { applyStyle, removeStyle, insertImage } from '../../utils/styleUtils';


const { Title, Text } = Typography;



const LegislativeNoticeEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const isCreate = id === 'create';
  const [form] = Form.useForm();
  const [legislativeNotice, setLegislativeNotice] = useState<LegislativeNotice | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  
  // 이미지 관련 상태
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageWidth, setImageWidth] = useState(300);
  const [imageHeight, setImageHeight] = useState(200);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageResizeModalVisible, setImageResizeModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [tableModalVisible, setTableModalVisible] = useState(false);
  const [tableRows, setTableRows] = useState(2);
  const [tableCols, setTableCols] = useState(2);
  
  // 에디터 관련 상태
  const contentRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef<string>('');

  // 표 삽입 함수
  const handleInsertTable = () => {
    const updatedContent = insertTable(contentRef, tableRows, tableCols);
    // 비동기적으로 폼 값 업데이트하여 순환 참조 방지
    setTimeout(() => {
      form.setFieldValue('content', updatedContent);
    }, 0);
    setTableModalVisible(false);
  };

  

  


  const fetchLegislativeNotice = useCallback(async () => {
    if (!id || isCreate) return;
    
    setLoading(true);
    try {
      const data = await legislativeNoticeService.getLegislativeNotice(id);
      setLegislativeNotice(data);
      
      // 첨부파일도 함께 조회
      try {
        const attachmentData = await attachmentService.getLegislativeNoticeAttachments(id);
        setAttachments(attachmentData);
      } catch (error) {
        setAttachments(data.attachments || []);
      }
      
      form.setFieldsValue({
        title: data.title,
        content: data.content,
        category: data.category,
        deadline_date: dayjs(data.deadline_date),
      });

      // Form 설정 후 contentEditable div 초기화
      setTimeout(() => {
        if (contentRef.current && data.content) {
          contentRef.current.innerHTML = data.content;
          lastContentRef.current = data.content;
        }
      }, 0);
    } catch (error: any) {
      message.error(t('error.legislativeNoticeLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, form, t, isCreate]);

  useEffect(() => {
    if (id && !isCreate) {
      fetchLegislativeNotice();
    } else if (isCreate) {
      // 생성 모드: 빈 내용으로 초기화
      if (contentRef.current) {
        contentRef.current.innerHTML = '';
        lastContentRef.current = '';
      }
    }
  }, [fetchLegislativeNotice, id, isCreate]);



  // 개정사항 테이블 텍스트 업데이트 함수
  const updateTableTexts = useCallback(() => {
    if (!contentRef.current) return;
    
    const amendmentTables = contentRef.current.querySelectorAll('.amendment-table');
    
    amendmentTables.forEach((table, index) => {
      // 제목 업데이트
      const titleElement = table.querySelector('.amendment-title');
      if (titleElement) {
        const newTitle = t('editor.amendmentTable');
        titleElement.textContent = newTitle;
        titleElement.innerHTML = newTitle;
      }
      
      // 테이블 헤더 업데이트
      const thElements = table.querySelectorAll('th');
      if (thElements.length >= 2) {
        const beforeText = t('editor.beforeChange');
        const afterText = t('editor.afterChange');
        thElements[0].textContent = beforeText;
        thElements[0].innerHTML = beforeText;
        thElements[1].textContent = afterText;
        thElements[1].innerHTML = afterText;
      }
      
      // 버튼 텍스트 업데이트 (div 요소)
      const addBtn = table.querySelector('.add-row-btn');
      const deleteBtn = table.querySelector('.delete-row-btn');
      if (addBtn) {
        const addText = t('editor.addRow');
        addBtn.innerHTML = addText;
        addBtn.textContent = addText;
        // 강제로 다시 설정
        setTimeout(() => {
          addBtn.innerHTML = addText;
          addBtn.textContent = addText;
        }, 10);
      }
      if (deleteBtn) {
        const deleteText = t('editor.deleteRow');
        deleteBtn.innerHTML = deleteText;
        deleteBtn.textContent = deleteText;
        // 강제로 다시 설정
        setTimeout(() => {
          deleteBtn.innerHTML = deleteText;
          deleteBtn.textContent = deleteText;
        }, 10);
      }
    });
  }, [t]);



  // 표 행 추가/삭제 버튼 동작을 위한 직접 이벤트 리스너 등록
  useEffect(() => {

    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('add-row-btn')) {
        e.preventDefault();
        e.stopPropagation();
        
        const controlsDiv = target.closest('.table-controls');
        if (!controlsDiv) return;
        
        const table = controlsDiv.previousElementSibling as HTMLTableElement;
        if (!table) return;
        
        const tbody = table.querySelector('tbody');
        if (!tbody) return;
        
        const newRow = document.createElement('tr');
        newRow.innerHTML = `
          <td contenteditable="true"></td>
          <td contenteditable="true"></td>
        `;
        tbody.appendChild(newRow);
        
        // 폼 값 업데이트
        if (contentRef.current) {
          setTimeout(() => {
            form.setFieldValue('content', contentRef.current!.innerHTML);
          }, 0);
        }
        
        // 새 행 추가 후 약간의 지연을 두고 텍스트 업데이트
        setTimeout(() => {
          updateTableTexts();
        }, 50);
      } else if (target.classList.contains('delete-row-btn')) {
        e.preventDefault();
        e.stopPropagation();
        
        const controlsDiv = target.closest('.table-controls');
        if (!controlsDiv) return;
        
        const table = controlsDiv.previousElementSibling as HTMLTableElement;
        if (!table) return;
        
        const tbody = table.querySelector('tbody');
        if (!tbody || tbody.children.length <= 1) return;
        
        const lastChild = tbody.lastElementChild;
        if (lastChild) {
          tbody.removeChild(lastChild);
          
          // 폼 값 업데이트
          if (contentRef.current) {
            setTimeout(() => {
              form.setFieldValue('content', contentRef.current!.innerHTML);
            }, 0);
          }
        }
      }
    };
    
    // document에 이벤트 리스너 등록 (더 안정적)
    document.addEventListener('click', handler);
    
    return () => {
      document.removeEventListener('click', handler);
    };
  }, [form, t, updateTableTexts]); // t 함수를 의존성에 추가

  // 언어 변경 시 텍스트 업데이트
  useEffect(() => {
    updateTableTexts();
  }, [t, updateTableTexts]);

  // 권한 확인
  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>{t('common.noPermission')}</Title>
        <Button type="primary" onClick={() => navigate('/legislative-notices')}>
          {t('legislativeNotices.detail.backToList')}
        </Button>
      </div>
    );
  }

  const handleSave = async (values: { 
    title: string; 
    content: string; 
    category: LegislativeNoticeCategory;
    deadline_date: dayjs.Dayjs;
  }) => {
    setSaving(true);
    try {
      const saveData = {
        ...values,
        deadline_date: values.deadline_date.toISOString(),
      };

      if (legislativeNotice) {
        // 기존 입법예고 수정
        await legislativeNoticeService.updateLegislativeNotice(legislativeNotice.legislative_notice_id, saveData);
        message.success(t('success.legislativeNoticeUpdate'));
        navigate(`/legislative-notices/${legislativeNotice.legislative_notice_id}`);
      } else {
        // 새 입법예고 생성
        await legislativeNoticeService.createLegislativeNotice(saveData);
        message.success(t('success.legislativeNoticeCreate'));
        navigate('/legislative-notices');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || t('error.legislativeNoticeSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (legislativeNotice) {
      navigate(`/legislative-notices/${legislativeNotice.legislative_notice_id}`);
    } else {
      navigate('/legislative-notices');
    }
  };



  // 이미지 삽입 함수
  const handleInsertImage = async () => {
    if (!imageUrl.trim() && !imageFile) {
      message.error(t('error.imageUrlOrFile'));
      return;
    }

    // 이미지 파일이 있는 경우 서버에 업로드
    if (imageFile) {
      try {
        if (!legislativeNotice?.legislative_notice_id) {
          message.error(t('error.saveLegislativeNoticeFirst'));
          return;
        }

        const uploadedImage = await attachmentService.uploadEditorImage(
          imageFile, 
          'legislative-notice', 
          legislativeNotice.legislative_notice_id
        );
        
        // 업로드된 이미지 URL을 사용하여 삽입
        insertImage(uploadedImage.file_path, form, contentRef);
        message.success(t('success.imageUpload'));
      } catch (error: any) {
        message.error(error.response?.data?.message || t('error.imageUpload'));
        return;
      }
    } else {
      // URL 이미지 삽입
      insertImage(imageUrl, form, contentRef);
    }
    
    setImageModalVisible(false);
    setImageUrl('');
    setImageFile(null);
    setImagePreview('');
  };

  const handleFileUpload = async (file: File) => {
    if (!legislativeNotice) {
      message.error(t('error.saveLegislativeNoticeFirst'));
      return;
    }

    setUploading(true);
    try {
      await attachmentService.uploadAttachment({
        file,
        legislativeNoticeId: legislativeNotice.legislative_notice_id
      });
      message.success('첨부파일이 업로드되었습니다.');
      
      // 첨부파일 목록 새로고침
      const attachmentData = await attachmentService.getLegislativeNoticeAttachments(legislativeNotice.legislative_notice_id);
      setAttachments(attachmentData);
    } catch (error: any) {
      message.error(error.response?.data?.message || '첨부파일 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileDelete = async (attachment: Attachment) => {
    try {
      await attachmentService.deleteAttachment(attachment.attachment_id);
      message.success('첨부파일이 삭제되었습니다.');
      
      // 첨부파일 목록 새로고침
      if (legislativeNotice) {
        const attachmentData = await attachmentService.getLegislativeNoticeAttachments(legislativeNotice.legislative_notice_id);
        setAttachments(attachmentData);
      }
    } catch (error: any) {
      message.error('첨부파일 삭제에 실패했습니다.');
    }
  };

  const handleFileDownload = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, attachment.file_name);
    } catch (error: any) {
      message.error('첨부파일 다운로드에 실패했습니다.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleImageFileChange = (file: File) => {
    setImageFile(file);
    setImageUrl('');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageFileRemove = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const handleImageResize = () => {
    if (!selectedImage) return;
    
    selectedImage.style.width = `${imageWidth}px`;
    selectedImage.style.height = `${imageHeight}px`;
    selectedImage.style.maxWidth = '100%';
    
    // form 업데이트
    const contentElement = contentRef.current;
    if (contentElement) {
      form.setFieldValue('content', contentElement.innerHTML);
    }
    
    message.success(t('editor.imageResized'));
    
    // 모달 닫기
    setImageResizeModalVisible(false);
    setSelectedImage(null);
  };

  const handleAutoResize = () => {
    if (!selectedImage) return;
    
    const maxWidth = 800;
    const maxHeight = 600;
    
    let width = selectedImage.naturalWidth;
    let height = selectedImage.naturalHeight;
    
    // 비율을 유지하면서 크기 조정
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }
    
    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }
    
    // 이미지가 최대 크기보다 작을 때는 원본 크기 유지하되 비율은 보장
    selectedImage.style.width = `${width}px`;
    selectedImage.style.height = `${height}px`;
    selectedImage.style.maxWidth = '100%';
    
    // form 업데이트
    const contentElement = contentRef.current;
    if (contentElement) {
      form.setFieldValue('content', contentElement.innerHTML);
    }
    
    message.success(t('editor.imageAutoResized'));
    
    // 모달 닫기
    setImageResizeModalVisible(false);
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('legislativeNotices.detail.loading')}</div>
      </div>
    );
  }

  return (
    <div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            style={{ marginRight: 16 }}
          >
            {t('common.cancel')}
          </Button>
          
          <Space>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={() => {
                // contentEditable div의 내용을 폼에 반영
                if (contentRef.current) {
                  form.setFieldValue('content', contentRef.current.innerHTML);
                }
                form.submit();
              }}
              loading={saving}
            >
              {t('common.save')}
            </Button>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            title: '',
            content: '',
            category: LegislativeNoticeCategory.KYOGYU,
            deadline_date: undefined,
          }}
        >
          <Form.Item
            name="category"
            label={t('legislativeNotices.form.category')}
            rules={[{ required: true, message: t('legislativeNotices.form.categoryRequired') }]}
          >
            <Select placeholder={t('legislativeNotices.form.categoryPlaceholder')}>
              <Select.Option value={LegislativeNoticeCategory.KYOGYU}>
                {t('legislativeNotices.categories.KYOGYU')}
              </Select.Option>
              <Select.Option value={LegislativeNoticeCategory.KYORYUNG}>
                {t('legislativeNotices.categories.KYORYUNG')}
              </Select.Option>
              <Select.Option value={LegislativeNoticeCategory.YEGYU}>
                {t('legislativeNotices.categories.YEGYU')}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label={t('legislativeNotices.form.title')}
            rules={[{ required: true, message: t('legislativeNotices.form.titleRequired') }]}
          >
            <Input placeholder={t('legislativeNotices.form.titlePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="deadline_date"
            label={t('legislativeNotices.form.deadlineDate')}
            rules={[{ required: true, message: t('legislativeNotices.form.deadlineDateRequired') }]}
          >
            <DatePicker 
              format="YYYY-MM-DD"
              placeholder={t('legislativeNotices.form.deadlineDatePlaceholder')}
              style={{ width: '100%' }}
              disabledDate={() => false}
            />
          </Form.Item>

          <Form.Item
            name="content"
            label={t('legislativeNotices.form.content')}
            rules={[{ required: true, message: t('legislativeNotices.form.contentRequired') }]}
          >
            {/* 스타일 툴바 - 내용 영역 바로 위에 위치 */}
            <div style={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderBottom: 'none',
              borderRadius: '6px 6px 0 0',
              padding: '12px',
              marginBottom: 0,
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            {/* 텍스트 스타일 버튼들 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('진하게', form, contentRef)}
              title={t('editor.bold')}
            >
              {t('editor.bold')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('왼쪽', form, contentRef)}
              title={t('editor.alignLeft')}
            >
              {t('editor.alignLeft')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('가운데', form, contentRef)}
              title={t('editor.alignCenter')}
            >
              {t('editor.alignCenter')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('오른쪽', form, contentRef)}
              title={t('editor.alignRight')}
            >
              {t('editor.alignRight')}
            </Button>
            
            {/* 구분선 */}
            <div style={{ width: '1px', backgroundColor: '#d9d9d9', margin: '0 4px', height: '20px' }} />
            
            {/* 법률 문서 스타일 버튼들 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('제목', form, contentRef)}
              title={t('editor.titleStyle')}
            >
              {t('editor.title')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('장', form, contentRef)}
              title={t('editor.chapterStyle')}
            >
              {t('editor.chapter')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('조', form, contentRef)}
              title={t('editor.articleStyle')}
            >
              {t('editor.article')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('항', form, contentRef)}
              title={t('editor.paragraphStyle')}
            >
              {t('editor.paragraph')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('호', form, contentRef)}
              title={t('editor.subParagraphStyle')}
            >
              {t('editor.subParagraph')}
            </Button>
            <Button 
              size="small" 
              danger
              onClick={() => removeStyle(form, contentRef)}
              title={t('editor.removeStyle')}
            >
              {t('editor.removeStyle')}
            </Button>
            
            {/* 구분선 */}
            <div style={{ width: '1px', backgroundColor: '#d9d9d9', margin: '0 4px', height: '20px' }} />
            
            {/* 표 삽입 버튼 */}
            <Button 
              size="small" 
              onClick={() => setTableModalVisible(true)}
              title={t('editor.insertTable')}
            >
              {t('editor.table')}
            </Button>
            {/* 개정사항 표 삽입 버튼 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('개정사항', form, contentRef)}
              title={t('editor.amendmentTable')}
            >
              {t('editor.amendment')}
            </Button>
            {/* 이미지 삽입 버튼 */}
            <Button
              size="small"
              icon={<PictureOutlined />}
              onClick={() => setImageModalVisible(true)}
              title={t('editor.insertImage')}
            >
              {t('editor.image')}
            </Button>
          </div>


            <div
              ref={contentRef}
              contentEditable={true}
              className="editor-content"
              style={{
                height: '600px',
                maxHeight: '600px',
                border: '1px solid #d9d9d9',
                borderTop: 'none',
                borderRadius: '0 0 6px 6px',
                padding: '16px',
                fontFamily: 'monospace',
                fontSize: '16px',
                lineHeight: '1.6',
                outline: 'none',
                overflowY: 'auto',
                backgroundColor: '#ffffff'
              }}
              onInput={(e) => {
                const target = e.target as HTMLDivElement;
                lastContentRef.current = target.innerHTML;
              }}
              onBlur={(e) => {
                const target = e.target as HTMLDivElement;
                const newContent = target.innerHTML;
                if (newContent !== lastContentRef.current) {
                  lastContentRef.current = newContent;
                  form.setFieldValue('content', newContent);
                }
              }}
            />
          </Form.Item>

          {/* 첨부파일 섹션 */}
          <div style={{ marginBottom: 16 }}>
                          <div style={{ 
                marginBottom: '8px', 
                fontWeight: '500', 
                color: 'rgba(0, 0, 0, 0.85)',
                fontSize: '14px'
              }}>
                {t('section.attachments')}
              </div>
            
            {/* 파일 업로드 */}
            <div style={{ marginBottom: 12 }}>
              <input
                type="file"
                id="file-upload"
                style={{ display: 'none' }}
                accept="*/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file);
                    // 파일 선택 초기화
                    e.target.value = '';
                  }
                }}
              />
              <Button 
                icon={<Upload />} 
                loading={uploading}
                disabled={!legislativeNotice}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                {t('button.fileSelect')}
              </Button>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                {!legislativeNotice ? t('text.saveLegislativeNoticeFirst') : t('text.allFileSupport')}
              </Text>
            </div>

            {/* 첨부파일 목록 */}
            {attachments.length > 0 && (
              <List
                size="small"
                dataSource={attachments}
                renderItem={(attachment) => (
                  <List.Item
                    style={{ 
                      padding: '8px 12px', 
                      border: '1px solid #f0f0f0', 
                      borderRadius: '4px',
                      marginBottom: '4px'
                    }}
                    actions={[
                      <Button
                        key="download"
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleFileDownload(attachment)}
                        title={t('legislativeNotices.form.download')}
                      />,
                      <Button
                        key="delete"
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleFileDelete(attachment)}
                        title={t('legislativeNotices.form.delete')}
                      />
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileOutlined style={{ fontSize: '16px', color: '#1890ff' }} />}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '13px' }}>
                            {attachment.file_name}
                          </span>
                          <Tag color="blue">{formatFileSize(attachment.file_size)}</Tag>
                        </div>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          {dayjs(attachment.uploaded_at).format('YYYY-MM-DD HH:mm')}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            )}

            {attachments.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px', 
                color: '#8c8c8c',
                border: '1px dashed #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#fafafa'
              }}>
                <FileOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                <div>{t('legislativeNotices.form.noAttachments')}</div>
              </div>
            )}
          </div>
        </Form>
      </Card>

      {/* 이미지 크기 조절 모달 */}
      <Modal
        title="이미지 크기 조절"
        open={imageResizeModalVisible}
        onOk={handleImageResize}
        onCancel={() => {
          setImageResizeModalVisible(false);
          setSelectedImage(null);
        }}
        width={500}
        footer={[
          <Button key="auto" onClick={handleAutoResize}>
            자동 크기 조정
          </Button>,
          <Button key="cancel" onClick={() => {
            setImageResizeModalVisible(false);
            setSelectedImage(null);
          }}>
            취소
          </Button>,
          <Button key="submit" type="primary" onClick={handleImageResize}>
            적용
          </Button>
        ]}
      >
        <Form layout="vertical">
          <Form.Item label="현재 이미지">
            {selectedImage && (
              <div style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                padding: '8px',
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <img
                  src={selectedImage.src}
                  alt="선택된 이미지"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '150px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="너비 (px)">
                <InputNumber
                  value={imageWidth}
                  onChange={(value) => setImageWidth(value || 300)}
                  min={50}
                  max={800}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="높이 (px)">
                <InputNumber
                  value={imageHeight}
                  onChange={(value) => setImageHeight(value || 200)}
                  min={50}
                  max={600}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f6f8fa', borderRadius: '6px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 <strong>자동 크기 조정</strong>: 이미지 비율을 유지하면서 최대 800x600 크기로 자동 조정합니다.
            </Text>
          </div>
        </Form>
      </Modal>

      {/* 이미지 삽입 모달 */}
      <Modal
        title="이미지 삽입"
        open={imageModalVisible}
        onOk={handleInsertImage}
        onCancel={() => {
          setImageModalVisible(false);
          setImageUrl('');
          setImageFile(null);
          setImagePreview('');
        }}
        width={600}
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="이미지 URL">
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="이미지 URL을 입력하세요"
                  disabled={!!imageFile}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="로컬 파일">
                <Upload
                  beforeUpload={() => false}
                  onChange={({ file }) => {
                    if (file instanceof File) {
                      handleImageFileChange(file);
                    }
                  }}
                  showUploadList={false}
                  accept="image/*"
                  disabled={!!imageUrl.trim()}
                >
                  <Button disabled={!!imageUrl.trim()}>
                    파일 선택
                  </Button>
                </Upload>
                {imageFile && (
                  <div style={{ marginTop: 8 }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {imageFile.name}
                    </span>
                    <Button
                      type="text"
                      size="small"
                      danger
                      onClick={handleImageFileRemove}
                      style={{ marginLeft: 8 }}
                    >
                      제거
                    </Button>
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* 이미지 미리보기 */}
          {(imagePreview || imageUrl) && (
            <Form.Item label="미리보기">
              <div style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                padding: '8px',
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <img
                  src={imagePreview || imageUrl}
                  alt="미리보기"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </Form.Item>
          )}

          {/* 자동 크기 조정 안내 */}
          <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 <strong>자동 크기 조정</strong>: 이미지가 삽입되면 비율을 유지하면서 자동으로 크기가 조정됩니다. 
              삽입 후 이미지를 클릭하여 크기를 수동으로 조정할 수도 있습니다.
            </Text>
          </div>
        </Form>
      </Modal>

      {/* 표 삽입 모달 */}
      <Modal
        title={t('editor.insertTable')}
        open={tableModalVisible}
        onOk={handleInsertTable}
        onCancel={() => setTableModalVisible(false)}
        okText={t('editor.insert')}
        cancelText={t('common.cancel')}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('editor.rowCount')}:</label>
          <InputNumber
            min={1}
            max={20}
            value={tableRows}
            onChange={(value) => setTableRows(value || 2)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('editor.columnCount')}:</label>
          <InputNumber
            min={1}
            max={10}
            value={tableCols}
            onChange={(value) => setTableCols(value || 2)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ fontSize: '12px', color: '#666' }}>
          {t('editor.tableInsertNote')}
        </div>
      </Modal>
    </div>
  );
};

export default LegislativeNoticeEdit; 